import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosRequestConfig } from 'axios';

class ApiClient {
  private static instance: ApiClient;
  private client: AxiosInstance;

  private constructor() {
    this.client = axios.create({
      baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Get Shopify-specific values from localStorage
        const domain = typeof window !== 'undefined' ? localStorage.getItem('chargezenDomain') || '' : '';
        const domainKey = domain?.replace('.myshopify.com', '');
        const authToken = typeof window !== 'undefined' ? localStorage.getItem(`${domainKey}_authToken`) || '' : '';
        const accessToken = typeof window !== 'undefined' ? localStorage.getItem(`${domainKey}_accessToken`) || '' : '';

        // Add Shopify-specific headers
        config.headers['X-Shopify-Access-Token'] = accessToken;
        config.headers['Authorization'] = authToken;
        config.headers['X-Shop-Domain'] = domain;

        // Log request details
        console.log('API Request:', {
          url: config.url,
          method: config.method,
          headers: config.headers,
          data: config.data
        });

        return config;
      },
      (error: AxiosError) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log successful response
        console.log('API Response:', {
          status: response.status,
          data: response.data,
          headers: response.headers
        });
        return response;
      },
      (error: AxiosError) => {
        // Log error details
        console.error('API Error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
          config: error.config
        });

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401) {
          // Only redirect on client side
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private convertConfig(config?: AxiosRequestConfig): InternalAxiosRequestConfig {
    const internalConfig: InternalAxiosRequestConfig = {
      headers: new axios.AxiosHeaders(),
      ...config as any
    };
    return internalConfig;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, this.convertConfig(config));
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, this.convertConfig(config));
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, this.convertConfig(config));
  }

  public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, this.convertConfig(config));
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, this.convertConfig(config));
  }
}

export const apiClient = ApiClient.getInstance(); 